import React from 'react';

export const getProfessionalErrorMessage = (error: string): string => {
  const raw = (error ?? '').toString();
  const trimmed = raw.trim();

  if (!trimmed) return 'An unexpected error occurred. Please try again.';

  const isHtml =
    /<!doctype html/i.test(trimmed) ||
    /<html[\s>]/i.test(trimmed) ||
    /<head[\s>]/i.test(trimmed) ||
    /<body[\s>]/i.test(trimmed) ||
    /<title>.*?<\/title>/i.test(trimmed) ||
    /<h1>.*?<\/h1>/i.test(trimmed);

  const normalized = isHtml
    ? trimmed
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : trimmed;

  const debugPatterns = [
    /Internal Server Error/i,
    /Server Error\s*\(500\)/i,
    /500\s*(Error)?/i,
    /Traceback \(most recent call last\)/i,
    /File ".*\.py"/i,
    /line \d+/i,
    /django\./i,
    /ProgrammingError/i,
    /OperationalError/i,
    /DatabaseError/i,
    /IntegrityError/i,
    /SyntaxError/i,
    /NameError/i,
    /AttributeError/i,
    /ImportError/i,
    /KeyError/i,
    /ValueError.*at.*line/i,
    /TypeError.*at.*line/i,
    /AssertionError/i,
    /raise.*exception/i,
    /Exception.*while.*processing/i,
    /stack.*trace/i,
    /DEBUG.*True/i,
    /settings\.DEBUG/i,
    /connection.*failed.*to.*database/i,
    /operational.*error.*database/i,
    /timeout.*expired/i,
    /gateway.*timeout/i,
    /service.*unavailable/i,
  ];

  const isTechnicalError =
    isHtml || debugPatterns.some((pattern) => pattern.test(normalized));

  if (isTechnicalError) {
    return 'Service is currently unavailable due to a system issue. Please try again in a few minutes. If the problem persists, contact support.';
  }

  const userFriendlyErrors = [
    /Invalid.*credentials/i,
    /Wrong.*password/i,
    /User.*not.*found/i,
    /Account.*locked/i,
    /Too.*many.*attempts/i,
    /OTP.*invalid/i,
    /OTP.*expired/i,
    /Session.*expired/i,
    /Invalid.*session/i,
    /Required.*field/i,
    /Validation.*error/i,
    /Please.*enter/i,
    /Must.*provide/i,
    /Password.*too.*weak/i,
    /Email.*already.*exists/i,
    /Phone.*already.*registered/i,
    /Invalid.*email/i,
    /Invalid.*phone/i,
    /last.*name.*required/i,
    /The two password fields.*didn'?t match/i,
    /Enter.*6.*digit.*code/i,
    /Enter.*strong.*password/i,
  ];

  if (userFriendlyErrors.some((pattern) => pattern.test(normalized))) {
    return normalized;
  }

  if (
    normalized.length > 200 ||
    normalized.includes('\n') ||
    /\s{2,}/.test(normalized)
  ) {
    return 'An unexpected error occurred. Please try again or contact support if the issue continues.';
  }

  return normalized;
};

export const renderAlertMessage = (error: string): React.ReactNode => {
  const message = getProfessionalErrorMessage(error);

  if (message.includes('unavailable')) {
    return (
      <div>
        <div>{message}</div>
      </div>
    );
  }

  return message;
};
